import { OptionWallet } from '@model/option-wallet';
import { Component, Input, OnInit } from '@angular/core';
import { Globals } from '@theme/utils/globals';
import { EmitterService } from '@service/emitter/emitter.service';
import { VALIDATION } from '@theme/utils/functions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { WalletService } from '@service/wallet/wallet.service';
import { WalletTransactionComponent } from '@client/wallet-transaction/wallet-transaction.component';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { ToastrService } from 'ngx-toastr';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { CustomerService } from '@service/customer/customer.service';

@Component({
  selector: 'app-wallet-transaction-list',
  templateUrl: './wallet-transaction-list.component.html',
  styleUrls: ['./wallet-transaction-list.component.scss']
})
export class WalletTransactionListComponent implements OnInit {
  @Input() customerUuid: string;
  @Input() validate: boolean = false;

  transactions: OptionWallet[] = [];
  dtOptions: any = {};
  global = { country: Globals.country, device: Globals.device };
  validation = VALIDATION;
  total = 0;
  dataSelected: any[] = [];
  form: FormGroup;
  loading: boolean = false;

  constructor(
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private walletService: WalletService,
    private customerService: CustomerService,
    private emitter: EmitterService,
    private toastr: ToastrService
  ) {
    this.newForm();
  }

  ngOnInit(): void {
    this.dtOptions = Globals.dataTable;
    if (this.customerUuid) {
      this.loadTransactions();
    }
    this.emitter.event.subscribe((data) => {
      if (data.action === 'WALLET_TRANSACTION') {
        this.loadTransactions();
      }
    });
  }

  newForm(): void {
    this.form = this.formBuilder.group({
      valueOne: [null],
      checked: [null],
      checkedAll: this.formBuilder.array([]),
    });
  }

  /**
   * Charger la liste des transactions
   */
  loadTransactions(): void {
    if (!this.customerUuid) {
      return;
    }

    this.loading = true;
    this.walletService.getTransactionsList(this.customerUuid).subscribe(
      (response: any[]) => {
        this.transactions = response || [];
        this.total = this.transactions.reduce((sum, t) => sum + (t.montant || 0), 0);
        this.loading = false;
      },
      (error) => {
        console.error('Erreur chargement transactions:', error);
        this.loading = false;
        this.toastr.error('Erreur lors du chargement des transactions', 'Erreur');
      }
    );
  }

  /**
   * Afficher les détails d'une transaction
   */
  showTransaction(transaction: OptionWallet): void {
    // TODO: Créer un composant show si nécessaire
    console.log('Afficher transaction:', transaction);
  }

  /**
   * Imprimer le reçu d'une transaction
   */
  printReceipt(transaction: OptionWallet): void {
    if (!transaction.uuid) {
      this.toastr.warning('Impossible d\'imprimer : transaction sans UUID', 'Attention');
      return;
    }

    const agencyKey = Globals.user.agencyKey;
    const userKey = Globals.user.uuid;

    if (!agencyKey || !userKey) {
      this.toastr.error('Informations utilisateur manquantes', 'Erreur');
      return;
    }

    this.walletService.printReceipt(transaction.uuid, agencyKey, userKey);
  }

  /**
   * Modifier une transaction
   */
  editTransaction(transaction: OptionWallet): void {
    if (transaction.etat === 'VALIDE') {
      this.toastr.warning('Impossible de modifier une transaction validée', 'Attention');
      return;
    }

    if (transaction.etat === 'ANNULE') {
      this.toastr.warning('Impossible de modifier une transaction annulée', 'Attention');
      return;
    }

    // Charger le client
    const customer = this.customerService.getCustomer();
    if (!customer || customer.uuid !== this.customerUuid) {
      this.customerService.getSingle(this.customerUuid).subscribe((res) => {
        this.customerService.setCustomer(res);
        this.openEditModal(transaction);
      });
    } else {
      this.openEditModal(transaction);
    }
  }

  /**
   * Ouvrir le modal d'édition
   */
  openEditModal(transaction: OptionWallet): void {
    // TODO: Créer un composant d'édition ou utiliser le même modal avec mode édition
    this.toastr.info('Fonctionnalité d\'édition à venir', 'Info');
  }

  /**
   * Valider une transaction
   */
  validateTransaction(transaction: OptionWallet): void {
    if (transaction.etat === 'VALIDE') {
      this.toastr.warning('Cette transaction est déjà validée', 'Attention');
      return;
    }

    Swal.fire({
      title: 'Valider la transaction',
      html: `
        <div class="text-left">
          <p><strong>Type :</strong> ${transaction.type === 'DEPOT' ? 'Dépôt' : 'Retrait'}</p>
          <p><strong>Montant :</strong> ${this.formatMontant(transaction.montant || 0)}</p>
          <p><strong>Libellé :</strong> ${transaction.libelle || '-'}</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Valider',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (result.isConfirmed && transaction.uuid) {
        this.walletService.validateTransaction(transaction.uuid).subscribe(
          (response) => {
            this.toastr.success('Transaction validée avec succès', 'Succès');
            this.loadTransactions();
            
            // Émettre un événement pour rafraîchir le wallet
            this.emitter.emit({
              action: 'WALLET_TRANSACTION_VALIDATED',
              payload: response
            });
          },
          (error) => {
            const errorMessage = error.error?.message || 'Erreur lors de la validation';
            this.toastr.error(errorMessage, 'Erreur');
          }
        );
      }
    });
  }

  /**
   * Annuler une transaction
   */
  cancelTransaction(transaction: OptionWallet): void {
    if (transaction.etat === 'ANNULE') {
      this.toastr.warning('Cette transaction est déjà annulée', 'Attention');
      return;
    }

    Swal.fire({
      title: 'Annuler la transaction',
      html: `
        <div class="text-left">
          <p><strong>Type :</strong> ${transaction.type === 'DEPOT' ? 'Dépôt' : 'Retrait'}</p>
          <p><strong>Montant :</strong> ${this.formatMontant(transaction.montant || 0)}</p>
          <p class="text-danger"><strong>Attention :</strong> Cette action inversera l'opération sur le wallet.</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmer l\'annulation',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (result.isConfirmed && transaction.uuid) {
        this.walletService.cancelTransaction(transaction.uuid).subscribe(
          (response) => {
            this.toastr.success('Transaction annulée avec succès', 'Succès');
            this.loadTransactions();
            
            // Émettre un événement pour rafraîchir le wallet
            this.emitter.emit({
              action: 'WALLET_TRANSACTION_CANCELLED',
              payload: response
            });
          },
          (error) => {
            const errorMessage = error.error?.message || 'Erreur lors de l\'annulation';
            this.toastr.error(errorMessage, 'Erreur');
          }
        );
      }
    });
  }

  /**
   * Supprimer une transaction
   */
  deleteTransaction(transaction: OptionWallet): void {
    if (transaction.etat === 'VALIDE') {
      this.toastr.warning('Impossible de supprimer une transaction validée. Annulez-la d\'abord.', 'Attention');
      return;
    }

    Swal.fire({
      title: 'Supprimer la transaction',
      text: 'Voulez-vous vraiment supprimer cette transaction ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (result.isConfirmed && transaction.uuid) {
        // TODO: Ajouter endpoint delete si nécessaire
        this.toastr.info('Fonctionnalité de suppression à venir', 'Info');
      }
    });
  }

  /**
   * Validation en masse
   */
  onCheckAll(event: any): void {
    const isChecked = event.target.checked;
    this.dataSelected = isChecked ? this.transactions.filter(t => t.etat === 'ATTENTE').slice() : [];
    this.updateAllCheckboxes(isChecked);
  }

  onCheckItem(item: OptionWallet): void {
    if (item.etat !== 'ATTENTE') {
      return; // Ne pas sélectionner si déjà validé ou annulé
    }
    const index = this.dataSelected.findIndex(t => t.uuid === item.uuid);
    if (index === -1) {
      this.dataSelected.push(item);
    } else {
      this.dataSelected.splice(index, 1);
    }
    this.checkIfAllChecked();
  }

  updateAllCheckboxes(isChecked: boolean): void {
    const checkboxes = document.querySelectorAll('.form-check-input:not(#checkAll)');
    checkboxes.forEach((checkbox: any) => {
      checkbox.checked = isChecked;
    });
  }

  checkIfAllChecked(): void {
    const allCheckboxes = document.querySelectorAll('.form-check-input:not(#checkAll)');
    const allChecked = Array.from(allCheckboxes).every((checkbox: HTMLElement) => (checkbox as HTMLInputElement).checked);
    const checkAllCheckbox = document.getElementById('checkAll') as HTMLInputElement;
    if (checkAllCheckbox) {
      checkAllCheckbox.checked = allChecked;
    }
  }

  /**
   * Valider plusieurs transactions
   */
  validateAll(): void {
    if (this.dataSelected.length === 0) {
      this.toastr.warning('Veuillez sélectionner au moins une transaction', 'Attention');
      return;
    }

    Swal.fire({
      title: 'Valider les transactions',
      html: `
        <div class="text-left">
          <p><strong>Nombre de transactions :</strong> ${this.dataSelected.length}</p>
          <p>Voulez-vous valider ces transactions ?</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Valider',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#28a745'
    }).then((result) => {
      if (result.isConfirmed) {
        let validated = 0;
        let errors = 0;

        this.dataSelected.forEach((transaction) => {
          if (transaction.uuid) {
            this.walletService.validateTransaction(transaction.uuid).subscribe(
              () => {
                validated++;
                if (validated + errors === this.dataSelected.length) {
                  this.toastr.success(`${validated} transaction(s) validée(s)`, 'Succès');
                  this.dataSelected = [];
                  this.loadTransactions();
                }
              },
              () => {
                errors++;
                if (validated + errors === this.dataSelected.length) {
                  this.toastr.warning(`${validated} validée(s), ${errors} erreur(s)`, 'Résultat');
                  this.dataSelected = [];
                  this.loadTransactions();
                }
              }
            );
          }
        });
      }
    });
  }

  /**
   * Formater un montant
   */
  formatMontant(montant: number): string {
    return this.walletService.formatMontant(montant);
  }

  /**
   * Vérifie si la transaction provient d'un paiement client
   */
  isFromPayment(transaction: OptionWallet): boolean {
    const ref = transaction.reference || '';
    return ref.startsWith('PAY-DEBIT-') || ref.startsWith('PAY-CREDIT-');
  }

  /**
   * Obtenir la classe CSS pour le badge d'état
   */
  getEtatBadgeClass(etat: string): string {
    switch (etat) {
      case 'VALIDE':
        return 'badge-success';
      case 'ATTENTE':
        return 'badge-warning';
      case 'ANNULE':
        return 'badge-danger';
      case 'INVALIDE':
        return 'badge-secondary';
      default:
        return 'badge-secondary';
    }
  }

  /**
   * Vérifie si le type est un crédit (argent qui rentre dans le wallet)
   */
  isTypeCredit(type: string): boolean {
    return type === 'DEPOT' || type === 'CREDIT';
  }

  /**
   * Libellé du type de transaction
   */
  getTypeLabel(type: string): string {
    switch (type) {
      case 'DEPOT': return 'Dépôt';
      case 'CREDIT': return 'Crédit';
      case 'RETRAIT': return 'Retrait';
      case 'DEBIT': return 'Débit';
      default: return type;
    }
  }

  /**
   * Obtenir la classe CSS pour le type de transaction
   */
  getTypeBadgeClass(type: string): string {
    return this.isTypeCredit(type) ? 'badge-info' : 'badge-danger';
  }

  get checkedAll() { return this.form.get('checkedAll') as FormArray; }
}

